import React from "react";
import { AbsoluteFill, Audio, Img, OffthreadVideo, Sequence, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { asset, mediaSrc } from "../kit/assets";
import { C, HEAD, Puffy, RESET_ATTR, RESET_CSS, Sfx, SfxPack, rnd, type SfxPackName } from "../kit/brand";
import { Crop, PickCard, SCOPED_CSS, rect, usePreloadScreens, type Anim, type Rect } from "../kit/crops";
import { MAP_PINS } from "../kit/screens.generated";
import { LOGO_MARK, LOGO_WORD } from "../kit/logo.generated";
import { BookedPill, LAND, PORT, Place, TapDot, cl, eio, eo, evenDots, mix, ramp, type Lay } from "../kit/kit";
import { FILL, LegIcon, PhotoMarker, appear, type MediaSrc } from "../kit/markers";
import { Title } from "../kit/title";
import { MapStage, type Project } from "../kit/MapStage";
import { BOOKING_MEDIA, BOOKING_MUSIC_FROM, type BookingMedia } from "./media";
import { PICK_LL, PIN_LL, STOP_LL, camAt, routeGeo } from "./scene";

// The booking flow of the mobile app, 34 s on its own clock (it ships with the hook slowed, 35.5 s:
// ./BookingIntroSlow.tsx). The map is the stage: a hook of stays lands on Bangkok, the route draws
// itself over Thailand, the camera dives into Bangkok's picks, flies the route to Chiang Mai, drops
// the price pins on real streets, books La Mai, pulls back to the whole trip and ends on the globe.
// The cards are crops of the settled design artboards (kit/crops.tsx).

export type BookingIntroProps = {
	layout: "landscape" | "portrait";
	// the audio pass renders without the map: it makes no sound and is the slow part
	noMap?: boolean;
	musicVolume?: number;
	sfxPack?: SfxPackName;
	// leave the effects out: the slowed film (./BookingIntroSlow.tsx) plays them itself, outside its Freeze
	noSfx?: boolean;
	// clips, photo and music; missing keys fall back to BOOKING_MEDIA (see ./media.ts)
	media?: Partial<BookingMedia>;
};

const LINE = "#557177";
const stopMedia = (m: BookingMedia): MediaSrc[] => [
	{ src: m.pool, video: true, clipFrames: 180 },
	{ src: m.doiSuthep, video: true, clipFrames: 160 },
	{ src: m.pai, pos: "35% 50%" },
];
// La Mai's photo is the one the artboards use, committed with them
const LA_MAI = "featureintro/screens/ok-lamai.jpg";
const STOP_FILL = [FILL.city, FILL.city, FILL.town];
const CARD_ROT = [-4, 3, -3];
const O = 180; // the checkout and confirmation cues are written from an earlier, shorter cut: they run 180 frames later
const O1R: Rect = { x: 0, y: 14, w: 390, h: 592 }; // from above the title: room to breathe

// ---------- hook: stays, one window, on the beat ----------
// each cut is one of the stay types of our own search filter (design/screens/bouw/nuitee-soorten.json),
// with that family's Puffy glyph, as the kind pills on the results map draw it
type Cut = { clip: keyof BookingMedia; word: string; icon: string; land: [number, number, number]; port: [number, number, number] };
const CUT = 15;
const CUTS: Cut[] = [
	{ clip: "bungalows", word: "Guest houses", icon: "cottage", land: [1480, 800, 60], port: [960, 640, 56] },
	{ clip: "treehouse", word: "Treehouses", icon: "villa", land: [840, 840, 420], port: [880, 880, 440] },
	{ clip: "jungle", word: "Resorts", icon: "island", land: [1560, 720, 360], port: [980, 640, 320] },
	{ clip: "boutique", word: "Hotels", icon: "bed", land: [640, 900, 70], port: [820, 1320, 70] },
	{ clip: "pool", word: "Apartments", icon: "apartment", land: [760, 920, 130], port: [880, 1180, 120] },
];
export const LAND_AT = 76;
const HOOK_END = 100;

export const BookingIntro: React.FC<BookingIntroProps> = ({ layout, noMap = false, musicVolume = 0.5, sfxPack = "sfx-hotel", noSfx = false, media }) => {
	usePreloadScreens();
	const m: BookingMedia = { ...BOOKING_MEDIA, ...media };
	const STOP_MEDIA = stopMedia(m);
	const land = layout === "landscape";
	const L: Lay = land ? LAND : { ...PORT, k: 1.9 };
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const sp = (at: number, cfg: { damping?: number; stiffness?: number; mass?: number } = {}) =>
		f < at ? 0 : spring({ frame: f - at, fps, config: { damping: 14, stiffness: 150, mass: 0.8, ...cfg } });
	const cam = camAt(f, land);
	const R = land ? 34 : 38;
	const zf = interpolate(cam.zoom, [2.6, 5.2], [0.3, 1], cl); // markers shrink with the globe

	const mapO = ramp(f, 66, 94, eio);
	const veil = ramp(f, 598, 612, eio) * (1 - ramp(f, 794, 810, eio)) * 0.86;
	const routeO = 1 - ramp(f, 226, 240) + ramp(f, 360, 372) * (1 - ramp(f, 462, 474)) + ramp(f, 808, 822) * (1 - ramp(f, 930, 946));

	const overlay = (project: Project) => {
		// ---------- the route ----------
		const legs = routeGeo().map((leg) =>
			leg.map(([lng, lat]) => {
				const p = project(lng, lat);
				return [p.x, p.y] as [number, number];
			})
		);
		const poly = [...legs[0], ...legs[1].slice(1)];
		const dots = routeO > 0.01 ? evenDots(poly, 16).filter((d) => d.x > -60 && d.x < L.W + 60 && d.y > -60 && d.y < L.H + 60) : [];
		const reveal = interpolate(f, [100, 160], [0, 1], cl);
		const stops = STOP_LL.map(([lng, lat]) => project(lng, lat));
		const legMid = (s: number) => {
			const leg = legs[s];
			return leg[Math.floor(leg.length / 2)];
		};
		const onLeg = (s: number, t: number) => {
			const leg = legs[s];
			const x = t * (leg.length - 1);
			const j = Math.min(leg.length - 2, Math.floor(x));
			const u = x - j;
			return [mix(u, leg[j][0], leg[j + 1][0]), mix(u, leg[j][1], leg[j + 1][1])];
		};
		const cardsOn = ramp(cam.zoom, 4.2, 5.0);

		// ---------- Bangkok picks ----------
		const pins = PICK_LL.map(([lng, lat]) => project(lng, lat));
		const order = [0, 1, 2].sort((a, b) => pins[a].x - pins[b].x);
		const pickS = land ? 1.8 : 1.9;
		const pickGap = land ? 290 : 300;
		const pickY = land ? 360 : 930;
		const pickCx = land ? 1340 : 540;
		const pickOut = ramp(f, 340, 356, eio);

		// ---------- Chiang Mai portal ----------
		const cmP = stops[1];
		const portalOpen = spring({ frame: f - 392, fps, config: { damping: 15, stiffness: 130 } }) * (f >= 392 ? 1 : 0);
		const portalClose = ramp(f, 436, 452, eio);
		const pt = portalOpen * (1 - portalClose);
		const cs = Math.round(R * 3.9);
		const big = land ? 620 : 760;
		const portal = f >= 392 && f < 452 && pt > 0.001;

		// ---------- search ----------
		const SR = rect("R2", "search");
		const searchPos = land ? { x: 1340, y: 176, s: 1.7 } : { x: 540, y: 690, s: 2.1 };
		const searchT = sp(462, { damping: 15 });
		const searchOut = ramp(f, 596, 610, eio);
		const r2: Anim = {};
		{
			const rv = ramp(f, 500, 516);
			const ring = ramp(f, 500, 526);
			r2['>[data-k="search"] > *:nth-child(n+2)'] = `opacity:${rv}; translate:${(1 - rv) * -18}px 0`;
			r2.search = `box-shadow:0 0 0 ${f >= 500 ? 4 + ring * 12 : 0}px rgba(19,200,146,${f >= 500 ? 0.4 * (1 - ring) + 0.1 : 0})`;
		}
		const pinS = land ? 1.35 : 1.6;
		const pinOrder = MAP_PINS.filter((p) => !p.sel);
		const LM = rect("R3", "lamaiCard");
		const lamaiEnd = land ? { x: 1700, y: 650, s: 0.95 } : { x: 540, y: 1520, s: 1.25 };
		const lamaiT = sp(552, { damping: 15, stiffness: 130 });
		const laMai = project(PIN_LL.rsel[0], PIN_LL.rsel[1]);

		return (
			<>
				{/* the route over Thailand */}
				{routeO > 0.01 && (
					<div style={{ position: "absolute", inset: 0, opacity: Math.min(1, routeO) }}>
						<svg style={{ position: "absolute", inset: 0, overflow: "visible" }} width={L.W} height={L.H}>
							{dots
								.filter((d) => d.p <= (f < 160 ? reveal : 1))
								.map((d, i) => (
									<circle key={i} cx={d.x} cy={d.y} r={4.2} fill={LINE} />
								))}
						</svg>
						{[0, 1].map((s) => {
							const [x, y] = f >= 364 && f < 446 && s === 0 ? onLeg(0, ramp(f, 366, 444, eio)) : legMid(s);
							return (
								<LegIcon
									key={s}
									x={x}
									y={y}
									size={(land ? 52 : 56) * (0.6 + 0.4 * zf)}
									glyph={s === 0 ? "train" : "bus"}
									t={sp([118, 144][s], { damping: 10, stiffness: 220 })}
								/>
							);
						})}
						{[0, 1, 2].map((i) => (
							<PhotoMarker
								key={i}
								x={stops[i].x}
								y={stops[i].y}
								R={R * zf}
								fill={STOP_FILL[i]}
								num={i + 1}
								media={cardsOn > 0 ? STOP_MEDIA[i] : undefined}
								mediaFrom={[60, 134, 158][i]}
								rot={CARD_ROT[i]}
								markerT={sp([94, 126, 150][i], { damping: 9, stiffness: 220 })}
								cardT={(i === 0 ? (f >= HOOK_END ? 1 : 0) : sp([134, 158][i - 1], { damping: 12, stiffness: 150 })) * cardsOn}
								captionT={appear(f, [104, 136, 160][i]) * cardsOn}
								caption={["Bangkok", "Chiang Mai", "Pai"][i]}
								booked={i === 1 ? ramp(f, 846, 855, (x) => x * x) : 0}
								bob={i}
								z={8}
								cardDx={[0, 0.35, -0.35][i] * cs * zf}
							/>
						))}
					</div>
				)}

				{/* Chiang Mai opens up while we fly there */}
				{portal &&
					(() => {
						const size = mix(pt, cs * zf, big);
						const cx = mix(pt, cmP.x, land ? 1340 : 540);
						const cy = mix(pt, cmP.y - R * 0.62 * zf - (cs * zf) / 2, land ? 560 : 1180);
						return (
							<div
								style={{
									position: "absolute",
									left: cx - size / 2,
									top: cy - size / 2,
									width: size,
									height: size * mix(pt, 1, land ? 0.72 : 1.1),
									borderRadius: mix(pt, 16 * (R / 23), 48),
									border: `${mix(pt, 3 * (R / 23), 6)}px solid #fff`,
									boxShadow: "0 30px 80px rgba(15,62,74,.3)",
									overflow: "hidden",
									background: C.ink,
									boxSizing: "border-box",
									zIndex: 30,
								}}
							>
								<Sequence from={f < 422 ? 392 : 422} layout="none" key={f < 422 ? "m" : "d"}>
									<OffthreadVideo src={mediaSrc(f < 422 ? m.monks : m.doiSuthep)} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
								</Sequence>
							</div>
						);
					})()}

				{/* Bangkok: our picks stand above their own hotels */}
				{f >= 248 &&
					f < 360 &&
					[0, 1, 2].map((i) => {
						const slot = order.indexOf(i);
						const pin = pins[i];
						const mk = sp(250 + slot * 4, { damping: 9, stiffness: 220 });
						const t = sp(262 + slot * 5, { damping: 13, stiffness: 140 });
						const tx = pickCx + (slot - 1) * pickGap;
						const ty = pickY;
						const x = mix(t * (1 - pickOut), pin.x, tx);
						const y = mix(t * (1 - pickOut), pin.y, ty);
						const s = mix(t * (1 - pickOut), 0.15, pickS);
						const cardBottom = y + (152 * s) / 2;
						return (
							<React.Fragment key={i}>
								<svg style={{ position: "absolute", inset: 0, overflow: "visible", opacity: ramp(t, 0.4, 0.9) * (1 - pickOut) }} width={L.W} height={L.H}>
									<line x1={x} y1={cardBottom} x2={pin.x} y2={pin.y} stroke="#fff" strokeWidth={4} strokeLinecap="round" />
									<line
										x1={x}
										y1={cardBottom}
										x2={pin.x}
										y2={pin.y}
										stroke={C.ink}
										strokeWidth={1.5}
										strokeDasharray="2 6"
										strokeLinecap="round"
										opacity={0.5}
									/>
								</svg>
								<PhotoMarker x={pin.x} y={pin.y} R={22} fill={FILL.hotels} glyph="bed" mediaFrom={0} markerT={mk} cardT={0} out={pickOut} z={9} />
								{t > 0.01 && (
									<div
										style={{
											position: "absolute",
											left: x - 62,
											top: y - 76,
											width: 124,
											height: 152,
											transform: `scale(${s}) rotate(${mix(t, 0, [-5, 2, 6][slot])}deg)`,
											opacity: Math.min(1, t * 2) * (1 - pickOut),
											filter: `drop-shadow(0 10px 18px rgba(15,62,74,.25))`,
											zIndex: 20 + slot,
										}}
									>
										<PickCard i={i} />
									</div>
								)}
							</React.Fragment>
						);
					})}
				{f >= 296 && f < 360 && (
					<Place
						cx={land ? 1340 : 540}
						cy={(land ? 930 : 1730) + (1 - sp(298)) * 80}
						w={rect("T1", "find").w}
						h={rect("T1", "find").h}
						s={land ? 1.7 : 2.2}
						o={Math.min(1, sp(298) * 2) * (1 - pickOut)}
						z={25}
					>
						<Crop code="T1" r={rect("T1", "find")} id="v5-find" radius={16} />
					</Place>
				)}

				{/* Chiang Mai: prices on real streets */}
				{f >= 500 &&
					f < 620 &&
					MAP_PINS.map((p) => {
						const i = pinOrder.indexOf(p);
						const at = p.sel ? 540 : 505 + i * 3;
						const s = sp(at, { damping: p.sel ? 7 : 9, stiffness: 240 });
						const ll = PIN_LL[p.k];
						const q = project(ll[0], ll[1]);
						const w = p.sel ? 76 : 52;
						const h = p.sel ? 34 : 26;
						return (
							<div
								key={p.k}
								style={{
									position: "absolute",
									left: q.x - w / 2,
									top: q.y - h,
									width: w,
									height: h,
									transformOrigin: "50% 100%",
									transform: `scale(${pinS * s * (p.sel ? 1 + 0.12 * Math.sin(Math.PI * ramp(f, 580, 592)) : 1)})`,
									opacity: 1 - searchOut,
									zIndex: p.sel ? 16 : 14,
									filter: "drop-shadow(0 3px 6px rgba(15,62,74,.25))",
								}}
							>
								<div data-scr="pin" style={{ position: "relative" }} dangerouslySetInnerHTML={{ __html: p.html }} />
							</div>
						);
					})}
				{f >= 460 && f < 612 && (
					<Place
						cx={searchPos.x}
						cy={searchPos.y}
						w={SR.w}
						h={SR.h}
						s={searchPos.s * (0.92 + 0.08 * searchT)}
						o={Math.min(1, searchT * 2) * (1 - searchOut)}
						z={40}
					>
						<Crop code="R2" r={SR} id="v5-search" anim={r2} radius={16} />
					</Place>
				)}
				{f >= 468 &&
					f < 512 &&
					[
						{ icon: "calendar", text: "22–25 Oct · 3 nights", at: 468, dx: -1 },
						{ icon: "suitcase", text: "2 adults, 1 child", at: 473, dx: 1 },
					].map((m, i) => {
						const p = sp(m.at, { damping: 11, stiffness: 200 });
						const ft = ramp(f, 484 + i * 3, 500 + i * 3, eio);
						const x = mix(ft, cmP.x + m.dx * 130, searchPos.x + m.dx * 110);
						const y = mix(ft, cmP.y + 70, searchPos.y) - Math.sin(Math.PI * ft) * 70;
						return (
							<div
								key={i}
								style={{
									position: "absolute",
									left: x,
									top: y,
									transform: `translate(-50%,-50%) scale(${p * mix(ft, 1.15, 0.6)})`,
									opacity: 1 - ramp(f, 498 + i * 3, 506 + i * 3),
									display: "inline-flex",
									alignItems: "center",
									gap: 10,
									height: 56,
									padding: "0 22px 0 10px",
									borderRadius: 28,
									background: "#fff",
									boxShadow: "0 10px 26px rgba(15,62,74,.18)",
									fontFamily: HEAD,
									fontWeight: 600,
									fontSize: 22,
									color: C.ink,
									whiteSpace: "nowrap",
									zIndex: 45,
								}}
							>
								{m.text}
							</div>
						);
					})}
				{f >= 548 && f < 612 && (
					<>
						<svg style={{ position: "absolute", inset: 0, overflow: "visible", opacity: ramp(lamaiT, 0.5, 1) * (1 - searchOut) }} width={L.W} height={L.H}>
							<line x1={lamaiEnd.x} y1={lamaiEnd.y} x2={laMai.x} y2={laMai.y} stroke="#fff" strokeWidth={4} strokeLinecap="round" />
						</svg>
						<Place
							cx={lamaiEnd.x}
							cy={mix(lamaiT, lamaiEnd.y + 520, lamaiEnd.y)}
							w={LM.w}
							h={LM.h}
							s={lamaiEnd.s * (1 - 0.04 * Math.sin(Math.PI * ramp(f, 584, 592)))}
							rot={mix(lamaiT, 10, 3)}
							o={Math.min(1, lamaiT * 2) * (1 - searchOut)}
							z={42}
						>
							<Crop code="R3" r={LM} id="v5-lamai" radius={20} />
						</Place>
					</>
				)}
				<TapDot x={lamaiEnd.x - 20} y={lamaiEnd.y - 60} at={584} size={land ? 58 : 70} />

				{/* La Mai, booked, on the map as we pull back */}
				{f >= 800 && f < 860 && (
					<PhotoMarker
						x={laMai.x}
						y={laMai.y}
						R={30}
						fill={FILL.hotels}
						glyph="bed"
						media={{ src: LA_MAI }}
						mediaFrom={806}
						rot={3}
						markerT={sp(804, { damping: 9, stiffness: 220 })}
						cardT={sp(808, { damping: 12, stiffness: 150 })}
						captionT={appear(f, 812)}
						caption="La Mai Hotel"
						booked={ramp(f, 814, 823, (x) => x * x)}
						out={ramp(f, 846, 858)}
						z={18}
					/>
				)}

				{/* the hook's windows, landing on stop 1 */}
				{f < HOOK_END && (
					<HookWindows
						lay={L}
						land={land}
						media={m}
						// exactly where PhotoMarker draws stop 1's card this frame (including its float)
						target={{
							x: stops[0].x,
							y: stops[0].y - R * 0.62 - cs / 2 + Math.sin(f / 22) * R * 0.08,
							size: cs,
							radius: 16 * (R / 23),
							edge: 3 * (R / 23),
							rot: CARD_ROT[0],
						}}
					/>
				)}

				{/* the close: tilted stays pop up on the world, each on its own spot */}
				{f >= 944 && <WorldStays project={project} land={land} />}

				{/* veil for checkout and confirmation */}
				{veil > 0.001 && <div style={{ position: "absolute", inset: 0, background: C.mint, opacity: veil, zIndex: 50 }} />}
			</>
		);
	};

	return (
		<AbsoluteFill {...RESET_ATTR} style={{ background: C.mint, overflow: "hidden" }}>
			<style>{`${RESET_CSS}\n${SCOPED_CSS}`}</style>
			{!noMap && <MapStage w={L.W} h={L.H} cam={cam} padding={cam.padding} mapOpacity={mapO} overlay={overlay} />}
			{/* soft light over the text column */}
			<div
				style={{
					position: "absolute",
					inset: 0,
					background: land
						? "linear-gradient(90deg, rgba(237,249,244,.95) 0%, rgba(237,249,244,.82) 30%, rgba(237,249,244,0) 52%)"
						: "linear-gradient(180deg, rgba(237,249,244,.95) 0%, rgba(237,249,244,.85) 22%, rgba(237,249,244,0) 36%)",
					opacity: mapO * (1 - ramp(f, 900, 930)),
					pointerEvents: "none",
				}}
			/>
			<Checkout lay={L} />
			<Words lay={L} />
			<EndCard lay={L} />
			{musicVolume > 0 && <BookingMusic src={m.music} volume={musicVolume} />}
			{!noSfx && (
				<SfxPack.Provider value={sfxPack}>
					<BookingCues />
				</SfxPack.Provider>
			)}
		</AbsoluteFill>
	);
};

// The music: the full Villa Penthouse track from BOOKING_MUSIC_FROM, faded out over the film's last 3 s
export const BookingMusic: React.FC<{ src: string; volume: number }> = ({ src, volume }) => {
	const { fps, durationInFrames } = useVideoConfig();
	return (
		<Audio
			src={mediaSrc(src)}
			trimBefore={BOOKING_MUSIC_FROM * fps}
			volume={(f) => volume * interpolate(f, [durationInFrames - 3 * fps, durationInFrames], [1, 0], cl)}
		/>
	);
};

// ---------- hook windows ----------
const HookWindows: React.FC<{
	lay: Lay;
	land: boolean;
	media: BookingMedia;
	target: { x: number; y: number; size: number; radius: number; edge: number; rot: number };
}> = ({ lay, land, media, target }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const cut = Math.min(CUTS.length - 1, Math.floor(f / CUT));
	const c = CUTS[cut];
	const shape = (x: Cut) => (land ? x.land : x.port);
	const k =
		cut === 0
			? spring({ frame: f, fps, config: { damping: 12, stiffness: 140 } })
			: spring({ frame: f - cut * CUT, fps, config: { damping: 14, stiffness: 210 } });
	const a = cut === 0 ? [shape(c)[0] * 0.6, shape(c)[1] * 0.6, 200] : shape(CUTS[cut - 1]);
	const b = shape(c);
	let w = mix(k, a[0], b[0]);
	let h = mix(k, a[1], b[1]);
	let r = mix(k, a[2], b[2]);
	let cx = lay.W / 2;
	let cy = land ? 540 : 900;
	let rot = cut === 0 ? mix(k, -6, 0) : Math.sin(cut * 1.7) * 1.5 * (1 - k);
	let edge = 0;
	const t = ramp(f, LAND_AT, HOOK_END, eio);
	if (t > 0) {
		cx = mix(t, cx, target.x);
		cy = mix(t, cy, target.y);
		w = mix(t, w, target.size);
		h = mix(t, h, target.size);
		r = mix(t, r, target.radius);
		rot = mix(t, 0, target.rot);
		edge = target.edge * t;
	}
	const wordK = spring({ frame: f - cut * CUT - 2, fps, config: { damping: 12, stiffness: 200, mass: 0.6 } });
	const wordOut = cut === CUTS.length - 1 ? ramp(f, LAND_AT - 4, LAND_AT + 4) : 0;
	const ph = land ? 92 : 100; // the label pill: white, dark glyph, the type in ink
	return (
		<>
			<div
				style={{
					position: "absolute",
					left: cx - w / 2,
					top: cy - h / 2,
					width: w,
					height: h,
					borderRadius: r,
					overflow: "hidden",
					transform: `rotate(${rot}deg) scale(${t > 0 ? 1 : 1 + 0.04 * (1 - k)})`,
					boxShadow: `0 ${30 - 20 * t}px ${80 - 55 * t}px rgba(15,62,74,.28)`,
					border: edge ? `${edge}px solid #fff` : undefined,
					boxSizing: "border-box",
					background: C.ink,
					zIndex: 80,
				}}
			>
				<Sequence from={cut * CUT} layout="none" key={cut}>
					<OffthreadVideo
						src={mediaSrc(media[c.clip])}
						muted
						style={{ width: "100%", height: "100%", objectFit: "cover", transform: `scale(${1.08 - 0.08 * k})` }}
					/>
				</Sequence>
			</div>
			<div
				style={{
					position: "absolute",
					left: cx,
					top: cy + h / 2 - ph * 0.5,
					transform: `translate(-50%, 0) translateY(${(1 - wordK) * 40}px) scale(${0.7 + 0.3 * wordK}) rotate(${(1 - wordK) * -6}deg)`,
					opacity: Math.min(1, wordK * 1.6) * (1 - wordOut),
					display: "inline-flex",
					alignItems: "center",
					gap: ph * 0.2,
					height: ph,
					padding: `0 ${ph * 0.42}px 0 ${ph * 0.14}px`,
					borderRadius: 999,
					background: "#fff",
					boxShadow: "0 14px 34px rgba(15,62,74,.28)",
					whiteSpace: "nowrap",
					zIndex: 82,
				}}
			>
				<span
					style={{
						width: ph * 0.72,
						height: ph * 0.72,
						borderRadius: 999,
						background: C.mint,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Puffy name={c.icon} size={ph * 0.5} color="#14202a" />
				</span>
				<span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: ph * 0.48, letterSpacing: "-0.02em", color: C.ink }}>{c.word}</span>
			</div>
		</>
	);
};

// ---------- checkout and confirmation, above the veil ----------
const Checkout: React.FC<{ lay: Lay }> = ({ lay: L }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const k = L.k;
	const sp = (at: number, cfg: { damping?: number; stiffness?: number; mass?: number } = {}) =>
		f < at ? 0 : spring({ frame: f - at, fps, config: { damping: 14, stiffness: 150, mass: 0.8, ...cfg } });
	if (f < 600 || f > 812) return null;
	const RC = rect("K1", "roomCard");
	const B1 = rect("K1", "book1");
	const roomT = sp(422 + O, { damping: 15 });
	const roomOut = ramp(f, 456 + O, 472 + O, eio);
	const roomY = L.top + 8 + (RC.h * k) / 2;
	const k1: Anim = {};
	if (f >= 450 + O && f < 460 + O) k1.book1 = `scale:${interpolate(f, [450 + O, 453 + O, 459 + O], [1, 0.9, 1], cl)}`;
	const PC = rect("P2", "payCard");
	const PB = rect("P2", "payBtn");
	const payT = sp(460 + O, { damping: 15 });
	const payOut = ramp(f, 512 + O, 528 + O, eio);
	const payY = L.top + 8 + (PC.h * k) / 2;
	const btnT = sp(468 + O, { damping: 14 });
	const btnY = payY + (PC.h * k) / 2 + 30 + (PB.h * k) / 2;
	const p2: Anim = {};
	if (f >= 504 + O && f < 512 + O) p2.payBtn = `scale:${interpolate(f, [504 + O, 507 + O, 511 + O], [1, 0.95, 1], cl)}`;
	const BK = rect("F1", "blk");
	const TK = rect("F1", "tick");
	const blkY = L.top + (BK.h * k) / 2;
	const tick = { x: L.sx + (TK.x + TK.w / 2 - (BK.x + BK.w / 2)) * k, y: blkY + (TK.y + TK.h / 2 - (BK.y + BK.h / 2)) * k };
	const morphT = ramp(f, 512 + O, 532 + O, eio);
	const confOut = ramp(f, 792, 806, eio);
	const f1: Anim = {};
	f1.tick = `opacity:${f < 535 + O ? 0 : 1}`;
	for (let i = 0; i < 8; i++) {
		const d = sp(536 + O + (i % 3), { damping: 12, stiffness: 140 });
		const r = rect("F1", `dot${i}`);
		f1[`dot${i}`] = `translate:${(TK.x + 38 - r.x) * (1 - d)}px ${(TK.y + 38 - r.y) * (1 - d)}px; scale:${1 + 1.4 * (1 - d)}; opacity:${f < 536 + O ? 0 : 0.8}`;
	}
	const tt = sp(540 + O, { damping: 12 });
	f1.title = `scale:${0.5 + 0.5 * tt}; opacity:${interpolate(tt, [0, 0.3], [0, 1], cl)}`;
	f1['>[data-k="blk"] > p:last-of-type'] = `opacity:${ramp(f, 548 + O, 560 + O)}`;
	const REF = rect("F1", "refCard");
	const HOT = rect("F1", "hotelCard");
	const refY = blkY + (BK.h * k) / 2 + 14 + (REF.h * k) / 2;
	const hotY = refY + (REF.h * k) / 2 + 14 + (HOT.h * k) / 2;
	return (
		<AbsoluteFill style={{ zIndex: 60 }}>
			{f < 474 + O && (
				<Place cx={L.sx + (1 - roomT) * 300} cy={roomY - 120 * roomOut} w={RC.w} h={RC.h} s={k} o={Math.min(1, roomT * 2) * (1 - roomOut)} z={30}>
					<Crop code="K1" r={RC} id="v5-room" anim={k1} radius={20} />
				</Place>
			)}
			<TapDot x={L.sx + (B1.x + B1.w / 2 - (RC.x + RC.w / 2)) * k} y={roomY + (B1.y + B1.h / 2 - (RC.y + RC.h / 2)) * k} at={450 + O} size={L.land ? 58 : 70} />
			{f >= 458 + O && f < 530 + O && (
				<Place cx={L.sx} cy={payY + (1 - payT) * 220} w={PC.w} h={PC.h} s={k * (1 - 0.06 * payOut)} o={Math.min(1, payT * 2) * (1 - payOut)} z={30}>
					<Crop code="P2" r={PC} id="v5-pay" radius={20} />
				</Place>
			)}
			{f >= 466 + O && f < 512 + O && (
				<Place cx={L.sx} cy={btnY + (1 - btnT) * 160} w={PB.w} h={PB.h} s={k} o={Math.min(1, btnT * 2)} z={32}>
					<Crop code="P2" r={PB} id="v5-btn" anim={p2} radius={26} shadow={false} />
				</Place>
			)}
			<TapDot x={L.sx} y={btnY} at={504 + O} size={L.land ? 58 : 70} />
			{f >= 512 + O && f < 541 + O && (
				<div
					style={{
						position: "absolute",
						left: mix(morphT, L.sx, tick.x) - (mix(morphT, PB.w, 76) * k) / 2,
						top: mix(morphT, btnY, tick.y) - (mix(morphT, PB.h, 76) * k) / 2,
						width: mix(morphT, PB.w, 76) * k,
						height: mix(morphT, PB.h, 76) * k,
						borderRadius: 999,
						background: C.ink,
						overflow: "hidden",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						opacity: 1 - ramp(f, 536 + O, 540 + O),
						zIndex: 70,
					}}
				>
					<div style={{ position: "absolute", inset: 0, background: C.greenDeep, opacity: ramp(f, 516 + O, 530 + O) }} />
					<div
						style={{
							position: "absolute",
							left: "50%",
							top: "50%",
							width: PB.w,
							height: PB.h,
							transform: `translate(-50%,-50%) scale(${k})`,
							opacity: 1 - ramp(f, 512 + O, 520 + O),
						}}
					>
						<Crop code="P2" r={PB} id="v5-btn2" radius={26} shadow={false} />
					</div>
					<svg style={{ position: "absolute" }} width={40 * k} height={40 * k} viewBox="0 0 24 24">
						<path
							d="M5 12.5l4.2 4.2L19 7"
							fill="none"
							stroke="#fff"
							strokeWidth={2.6}
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeDasharray={24}
							strokeDashoffset={24 * (1 - ramp(f, 524 + O, 536 + O))}
						/>
					</svg>
				</div>
			)}
			{f >= 533 + O && (
				<>
					<Place cx={L.sx} cy={blkY} w={BK.w} h={BK.h} s={k} o={ramp(f, 533 + O, 537 + O) * (1 - confOut)} z={40}>
						<Crop code="F1" r={BK} id="v5-blk" anim={f1} radius={0} shadow={false} transparent />
					</Place>
					<Place
						cx={L.sx}
						cy={refY + (1 - sp(552 + O, { damping: 15 })) * 90}
						w={REF.w}
						h={REF.h}
						s={k}
						o={Math.min(1, sp(552 + O) * 2) * (1 - confOut)}
						z={38}
					>
						<Crop code="F1" r={REF} id="v5-ref" radius={18} />
					</Place>
					<Place
						cx={L.sx}
						cy={hotY + (1 - sp(560 + O, { damping: 15 })) * 110}
						w={HOT.w}
						h={HOT.h}
						s={k}
						o={Math.min(1, sp(560 + O) * 2) * (1 - confOut)}
						z={36}
					>
						<Crop code="F1" r={HOT} id="v5-hotel" radius={18} />
					</Place>
					{Array.from({ length: 44 }, (_, i) => {
						const t = (f - 536 - O) / fps;
						if (t < 0) return null;
						const ang = -Math.PI / 2 + (rnd(i) - 0.5) * Math.PI * 1.6;
						const v = (700 + rnd(i + 100) * 1100) * (L.land ? 0.8 : 1);
						const col = [C.green, C.greenText, C.amber, C.blush][i % 4];
						const round = i % 2 === 0;
						return (
							<div
								key={i}
								style={{
									position: "absolute",
									left: tick.x + Math.cos(ang) * v * t,
									top: tick.y + Math.sin(ang) * v * t + 1400 * t * t,
									width: round ? 16 : 12,
									height: round ? 16 : 26,
									borderRadius: round ? 8 : 4,
									background: col,
									transform: `rotate(${rnd(i + 7) * 360 + t * 600 * (rnd(i + 3) - 0.5)}deg)`,
									opacity: interpolate(t, [0, 0.08, 2.2, 2.8], [0, 1, 1, 0], cl) * (1 - confOut),
									zIndex: 60,
								}}
							/>
						);
					})}
				</>
			)}
		</AbsoluteFill>
	);
};

// ---------- the close: stays on the world ----------
// img: a picture of the artboards (public/featureintro/screens)
const STAYS: { ll: [number, number]; img: string; name: string; at: number; booked?: boolean }[] = [
	{ ll: [12.4868, 41.8948], img: "hotel-1.jpg", name: "Palazzo Grillo", at: 946 },
	{ ll: [100.5145, 13.7236], img: "kb-mandarin.jpg", name: "Mandarin Oriental", at: 952 },
	{ ll: [100.585062, 13.68043], img: "kb-treehouse.jpg", name: "Bangkok Tree House", at: 958 },
	{ ll: [99.006151, 18.763801], img: "ok-lamai.jpg", name: "La Mai Hotel", at: 964, booked: true },
	{ ll: [98.982451, 18.790668], img: "ok-decharme.jpg", name: "De Charme", at: 970 },
	{ ll: [98.4399, 19.3585], img: "pai-village.jpg", name: "Pai Village", at: 976 },
];
const STAY_ROT = [-7, 5, -4, 6, -6, 4];

const WorldStays: React.FC<{ project: Project; land: boolean }> = ({ project, land }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const size = land ? 150 : 158;
	const pts = STAYS.map((st) => project(st.ll[0], st.ll[1]));
	// the Thai stays sit within a few pixels of each other on the globe: fan them out around Thailand
	const thai = pts.slice(1);
	const cx = thai.reduce((a, p) => a + p.x, 0) / thai.length;
	const cy = thai.reduce((a, p) => a + p.y, 0) / thai.length;
	// wide enough that no two cards touch, leaning left so the last one stays in frame
	const fan = land ? [-122, -82, -42, -2, 38] : [-112, -72, -32, 8, 48];
	const radius = land ? 290 : 275;
	return (
		<>
			{STAYS.map((st, i) => {
				const t = f < st.at ? 0 : spring({ frame: f - st.at, fps, config: { damping: 10, stiffness: 170, mass: 0.7 } });
				if (t <= 0) return null;
				const a = pts[i];
				const target =
					i === 0
						? { x: a.x, y: a.y - 150 }
						: { x: cx + Math.sin((fan[i - 1] * Math.PI) / 180) * radius, y: cy - Math.cos((fan[i - 1] * Math.PI) / 180) * radius };
				const bob = Math.sin((f + i * 11) / 18) * 5 * t;
				const x = mix(t, a.x, target.x);
				const y = mix(t, a.y, target.y) + bob;
				const booked = st.booked ? ramp(f, st.at + 12, st.at + 20, (q) => q * q) : 0;
				return (
					<React.Fragment key={st.name}>
						<svg style={{ position: "absolute", inset: 0, overflow: "visible" }} width={4000} height={4000}>
							<line x1={a.x} y1={a.y} x2={x} y2={y + (size * t) / 2} stroke="#fff" strokeWidth={3} strokeLinecap="round" opacity={Math.min(1, t)} />
							<circle cx={a.x} cy={a.y} r={7} fill={C.green} stroke="#fff" strokeWidth={3} />
						</svg>
						<div
							style={{
								position: "absolute",
								left: x - size / 2,
								top: y - size / 2,
								width: size,
								height: size,
								transform: `scale(${0.2 + 0.8 * t}) rotate(${mix(t, STAY_ROT[i] * 3, STAY_ROT[i])}deg)`,
								borderRadius: 22,
								border: "5px solid #fff",
								boxShadow: "0 14px 30px rgba(15,62,74,.28)",
								overflow: "hidden",
								background: "#fff",
								zIndex: 30 + i,
							}}
						>
							<Img src={asset(`screens/${st.img}`)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
						</div>
						<div
							style={{
								position: "absolute",
								left: x,
								top: y + size / 2 + 8,
								transform: `translateX(-50%) scale(${t})`,
								whiteSpace: "nowrap",
								font: "600 18px Rubik",
								color: C.ink,
								background: "#fff",
								borderRadius: 999,
								padding: "4px 12px",
								boxShadow: "0 3px 8px rgba(15,62,74,.25)",
								zIndex: 40 + i,
							}}
						>
							{st.name}
						</div>
						{booked > 0 && (
							<div
								style={{
									position: "absolute",
									left: x - 39,
									top: y + size / 2 + 44,
									width: 78,
									height: 22,
									transformOrigin: "50% 0%",
									transform: `scale(${mix(booked, 4, 1.3)}) rotate(${mix(booked, -12, 0)}deg)`,
									opacity: Math.min(1, booked * 3),
									zIndex: 45,
								}}
							>
								<BookedPill />
							</div>
						)}
					</React.Fragment>
				);
			})}
		</>
	);
};

// ---------- words ----------
const Words: React.FC<{ lay: Lay }> = ({ lay: L }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	const o1T = f < 852 ? 0 : spring({ frame: f - 852, fps, config: { damping: 15, stiffness: 150, mass: 0.8 } });
	const o1Out = ramp(f, 902, 914, eio);
	const o1: Anim = {};
	["o1", "o2", "o3"].forEach((key, i) => {
		const c = f < 862 + i * 5 ? 0 : spring({ frame: f - (862 + i * 5), fps, config: { damping: 14, stiffness: 170, mass: 0.8 } });
		o1[key] = `translate:${(1 - c) * 360}px 0; opacity:${Math.min(1, c * 2)}`;
	});
	const pos = L.land ? { x: 1650, y: 560, s: 1.35 } : { x: 540, y: 1430, s: 1.55 };
	return (
		<>
			{f >= 850 && f < 916 && (
				<Place cx={pos.x} cy={pos.y + (1 - o1T) * 180} w={O1R.w} h={O1R.h} s={pos.s * (1 - 0.05 * o1Out)} o={Math.min(1, o1T * 2) * (1 - o1Out)} z={30}>
					<Crop code="O1" r={O1R} id="v5-o1" anim={o1} radius={34} />
				</Place>
			)}
			<Title
				lay={L}
				at={106}
				out={214}
				eyebrow="NEW IN STIPPL"
				sticker
				lines={[
					["Planned", "the", "trip?"],
					["Now", "book", "the", "beds."],
				]}
				sub="Hotels for every stop, right inside your plan."
			/>
			<Title lay={L} at={232} out={340} lines={[["Handpicked"], ["for", "every", "stop."]]} sub="From budget to luxury, bookable right here." />
			<Title
				lay={L}
				at={366}
				out={440}
				lines={[
					["Next", "up:"],
					["Chiang", "Mai."],
				]}
				sub="22–25 Oct · no stay yet"
			/>
			<Title
				lay={L}
				at={456}
				out={588}
				lines={[
					["Dates?", "Guests?"],
					["Already", "filled", "in."],
				]}
				sub="Every search starts from your trip."
			/>
			<Title
				lay={L}
				at={604}
				out={708}
				lines={[
					["Found", "the", "one?"],
					["Book", "in", "two", "taps."],
				]}
				sub="Secure checkout, right in the app."
				taps={{ line: 1, word: 3, at: 630 }}
			/>
			<Title lay={L} at={720} out={792} stamp lines={[["Booked."], ["Just", "like", "that."]]} sub="Reference, times and room, straight away." />
			<Title
				lay={L}
				at={816}
				out={846}
				lines={[
					["Straight", "into"],
					["your", "plan."],
				]}
			/>
			<Title
				lay={L}
				at={852}
				out={904}
				lines={[
					["Every", "booking."],
					["One", "place."],
				]}
				sub="Upcoming, past and cancelled. Even stays booked elsewhere."
			/>
		</>
	);
};

// ---------- end: logo, line, store badges; the globe carries the route ----------
const EndCard: React.FC<{ lay: Lay }> = ({ lay: L }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	if (f < 912) return null;
	const markT = spring({ frame: f - 914, fps, config: { damping: 10, stiffness: 170, mass: 0.8 } });
	const hop = f >= 960 ? Math.sin(Math.PI * ramp(f, 960, 974, eo)) : 0;
	const wordT = ramp(f, 920, 938, eo);
	const land = L.land;
	const logo = land ? { x: 150, y: 330, w: 300 } : { x: 540 - 180, y: 300, w: 360 };
	const badgeT = (at: number) => (f < at ? 0 : spring({ frame: f - at, fps, config: { damping: 12, stiffness: 180, mass: 0.7 } }));
	const bh = land ? 72 : 84;
	return (
		<>
			<svg
				style={{ position: "absolute", left: logo.x, top: logo.y, overflow: "visible", zIndex: 70 }}
				width={logo.w}
				height={(logo.w * 30) / 113}
				viewBox="0 0 113 30"
			>
				<g transform={`translate(11 15) scale(${markT * (1 + 0.12 * hop)}) rotate(${(1 - markT) * -30}) translate(-11 -15)`}>
					{LOGO_MARK.map((d, i) => (
						<path key={i} d={d} fill="#13C892" />
					))}
				</g>
				<g style={{ clipPath: `inset(0 ${(1 - wordT) * 100}% 0 0)` }} opacity={wordT}>
					{LOGO_WORD.map((d, i) => (
						<path key={i} d={d} fill="#0F3E4A" />
					))}
				</g>
			</svg>
			<Title
				lay={L}
				at={928}
				out={99999}
				lines={[
					["Plan", "the", "trip."],
					["Book", "every", "stay."],
				]}
				pos={land ? { x: 150, top: 440, align: "left", size: 64 } : { top: 430, align: "center", size: 70 }}
			/>
			<div
				style={{
					position: "absolute",
					left: land ? 150 : 0,
					right: land ? undefined : 0,
					top: land ? 630 : 640,
					display: "flex",
					justifyContent: land ? "flex-start" : "center",
					gap: 18,
					zIndex: 70,
				}}
			>
				{["badge-apple-appstore.svg", "badge-playstore.svg"].map((b, i) => {
					const t = badgeT(944 + i * 6);
					return (
						<Img
							key={b}
							src={asset(`badges/${b}`)}
							style={{
								height: bh,
								width: (bh * 127) / 38,
								opacity: Math.min(1, t * 2),
								transform: `translateY(${(1 - t) * 30}px) scale(${0.85 + 0.15 * t})`,
								filter: "drop-shadow(0 8px 18px rgba(15,62,74,.18))",
							}}
						/>
					);
				})}
			</div>
		</>
	);
};

// every effect of the film, on the film's own clock; always mounted, each cue a Sequence of its own
export const BookingCues: React.FC = () => (
	<>
		{CUTS.map((_, i) => (
			<Sfx key={"h" + i} at={i * CUT} name={i % 2 ? "swoosh" : "whoosh"} volume={0.4} />
		))}
		<Sfx at={LAND_AT} name="swoosh" volume={0.4} />
		<Sfx at={98} name="pop" volume={0.45} />
		<Sfx at={118} name="pop-lo" volume={0.35} />
		<Sfx at={126} name="plink2" volume={0.4} />
		<Sfx at={144} name="pop-lo" volume={0.35} />
		<Sfx at={150} name="plink3" volume={0.4} />
		<Sfx at={220} name="whoosh" volume={0.55} />
		{[250, 254, 258].map((a, i) => (
			<Sfx key={"b" + a} at={a} name={`plink${i + 1}`} volume={0.35} />
		))}
		{[262, 267, 272].map((a, i) => (
			<Sfx key={"p" + a} at={a} name={["pop-lo", "pop", "pop-hi"][i]} volume={0.55} />
		))}
		<Sfx at={298} name="swoosh" volume={0.3} />
		<Sfx at={340} name="swoosh" volume={0.4} />
		<Sfx at={360} name="whoosh" volume={0.6} />
		<Sfx at={392} name="whoosh" volume={0.45} />
		<Sfx at={436} name="swoosh" volume={0.4} />
		<Sfx at={462} name="pop" volume={0.45} />
		<Sfx at={468} name="pop-lo" volume={0.45} />
		<Sfx at={473} name="pop" volume={0.45} />
		<Sfx at={484} name="whoosh" volume={0.35} />
		<Sfx at={500} name="ding" volume={0.4} />
		{Array.from({ length: 9 }, (_, i) => (
			<Sfx key={"pin" + i} at={505 + i * 3} name={`plink${i % 6}`} volume={0.28} />
		))}
		<Sfx at={540} name="pop-hi" volume={0.5} />
		<Sfx at={552} name="whoosh" volume={0.4} />
		<Sfx at={584} name="tap" volume={0.8} />
		<Sfx at={598} name="swoosh" volume={0.35} />
		<Sfx at={602} name="whoosh" volume={0.4} />
		<Sfx at={630} name="tap" volume={0.8} />
		<Sfx at={638} name="swoosh" volume={0.35} />
		<Sfx at={648} name="pop" volume={0.4} />
		<Sfx at={684} name="tap" volume={0.8} />
		<Sfx at={692} name="swoosh" volume={0.4} />
		<Sfx at={714} name="chime" volume={0.75} />
		<Sfx at={717} name="sparkle" volume={0.55} />
		<Sfx at={720} name="stamp" volume={0.5} />
		<Sfx at={732} name="pop-lo" volume={0.4} />
		<Sfx at={740} name="pop" volume={0.4} />
		<Sfx at={792} name="swoosh" volume={0.35} />
		<Sfx at={808} name="whoosh" volume={0.5} />
		<Sfx at={821} name="stamp" volume={0.5} />
		<Sfx at={853} name="stamp" volume={0.85} />
		<Sfx at={862} name="whoosh" volume={0.45} />
		{[872, 877, 882].map((a, i) => (
			<Sfx key={"o" + a} at={a} name={["pop-lo", "pop", "pop-hi"][i]} volume={0.4} />
		))}
		<Sfx at={900} name="whoosh" volume={0.5} />
		<Sfx at={916} name="pop-hi" volume={0.55} />
		<Sfx at={924} name="swoosh" volume={0.35} />
		<Sfx at={944} name="pop" volume={0.4} />
		<Sfx at={950} name="pop-hi" volume={0.4} />
		{STAYS.map((st, i) => (
			<Sfx key={st.name} at={st.at} name={`plink${i % 6}`} volume={0.4} />
		))}
		<Sfx at={982} name="stamp" volume={0.55} />
		<Sfx at={960} name="ding" volume={0.45} />
	</>
);
